<script setup>
import { formatNumber } from '@/composables/useFormat'
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Report',
  },
})

// Addendum 10, Modul AP — tracking kedaluwarsa SEDERHANA: laporan ini
// sekadar list + peringatan (bukan FEFO/pengurangan stok otomatis). Datanya
// murni dari product_batches, diisi opsional saat konfirmasi Purchase
// Receiving (lihat PurchaseService::confirmReceiving()) — kalau tenant tidak
// pernah mengisi expiry_date sama sekali, laporan ini akan selalu kosong,
// tidak ada dampak apa pun ke alur stok/Purchase Receiving yang sudah ada.
const loading = ref(false)
const days = ref(30)
const expiredCount = ref(0)
const items = ref([])

const headers = [
  { title: 'Bahan Baku', key: 'ingredient_name' },
  { title: 'Qty Diterima', key: 'quantity_received', align: 'end' },
  { title: 'Tanggal Terima', key: 'received_at' },
  { title: 'Kedaluwarsa', key: 'expiry_date' },
  { title: 'Sisa Hari', key: 'days_left', align: 'end' },
  { title: 'Status', key: 'is_expired', align: 'center', sortable: false },
]

const fetchReport = async () => {
  loading.value = true
  try {
    const response = await useApi('/api/v1/reports/expiring-stock', {
      query: { days: days.value },
    }).get()

    const data = response?.data ?? response

    items.value = data?.items ?? []
    expiredCount.value = data?.expired_count ?? 0
  } catch (err) {
    console.error('❌ Expiring stock report error:', err)
  } finally {
    loading.value = false
  }
}

onMounted(fetchReport)

const formatDate = iso => {
  if (!iso) return '-'

  return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
}

const statusMeta = item => {
  if (item.is_expired) return { color: 'error', label: 'Sudah Lewat' }
  if (item.days_left <= 7) return { color: 'error', label: 'Mendesak' }
  if (item.days_left <= 14) return { color: 'warning', label: 'Segera' }

  return { color: 'success', label: 'Aman' }
}
</script>

<template>
  <div>
    <VRow class="mb-4 align-center">
      <VCol cols="12" md="6">
        <h4 class="text-h4 font-weight-medium mb-1">Stok Mendekati Kedaluwarsa</h4>
        <p class="text-body-2 text-medium-emphasis">
          Batch bahan baku yang tanggal kedaluwarsanya diisi saat Purchase Receiving
        </p>
      </VCol>
    </VRow>

    <VAlert type="info" variant="tonal" density="compact" class="mb-4">
      Ini hanya catatan &amp; peringatan (fitur Retail) — stok tetap dikurangi seperti
      biasa saat penjualan, laporan ini tidak mengubah alur stok/Purchase Receiving
      sama sekali. Baris hanya muncul kalau tanggal kedaluwarsa diisi saat konfirmasi
      receiving; itu opsional, tidak semua barang punya expiry.
    </VAlert>

    <VCard class="mb-4">
      <VCardText>
        <VRow align="center">
          <VCol cols="12" sm="3">
            <VTextField
              v-model.number="days"
              type="number"
              min="1"
              max="365"
              label="Tampilkan dalam (hari)"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol cols="12" sm="2">
            <VBtn
              color="primary"
              block
              :loading="loading"
              prepend-icon="ri-refresh-line"
              @click="fetchReport"
            >
              Refresh
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <VRow class="mb-4">
      <VCol cols="12" sm="6">
        <VCard>
          <VCardText class="d-flex align-center gap-3">
            <VAvatar color="info" variant="tonal" size="44">
              <VIcon icon="ri-time-line" />
            </VAvatar>
            <div>
              <div class="text-caption text-medium-emphasis">Batch Ditemukan</div>
              <div class="text-h6 font-weight-bold text-info">
                {{ formatNumber(items.length) }} batch
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
      <VCol cols="12" sm="6">
        <VCard>
          <VCardText class="d-flex align-center gap-3">
            <VAvatar :color="expiredCount > 0 ? 'error' : 'success'" variant="tonal" size="44">
              <VIcon icon="ri-error-warning-line" />
            </VAvatar>
            <div>
              <div class="text-caption text-medium-emphasis">Sudah Lewat Tanggal</div>
              <div
                class="text-h6 font-weight-bold"
                :class="expiredCount > 0 ? 'text-error' : 'text-success'"
              >
                {{ formatNumber(expiredCount) }} batch
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VCard :loading="loading">
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        density="compact"
        hover
        :items-per-page="25"
      >
        <template #item.ingredient_name="{ item }">
          <span class="text-body-2 font-weight-medium">{{ item.ingredient_name }}</span>
        </template>

        <template #item.quantity_received="{ item }">
          {{ formatNumber(item.quantity_received) }} {{ item.unit }}
        </template>

        <template #item.received_at="{ item }">
          {{ formatDate(item.received_at) }}
        </template>

        <template #item.expiry_date="{ item }">
          {{ formatDate(item.expiry_date) }}
        </template>

        <template #item.days_left="{ item }">
          <span :class="item.is_expired ? 'text-error font-weight-bold' : ''">
            {{ item.days_left }} hari
          </span>
        </template>

        <template #item.is_expired="{ item }">
          <VChip :color="statusMeta(item).color" size="small" variant="tonal">
            {{ statusMeta(item).label }}
          </VChip>
        </template>

        <template #no-data>
          <div class="text-center py-8">
            <VIcon icon="ri-calendar-check-line" size="48" color="secondary" class="mb-2" />
            <p class="text-body-2 text-medium-emphasis">
              Tidak ada batch yang mendekati kedaluwarsa dalam {{ days }} hari ke depan
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>
  </div>
</template>
